import React, { useEffect, useCallback } from 'react';

import ChoiceInput from '../../../../components/ChoiceInput';
import choiceInputTypes from '../../../../components/ChoiceInput/choiceInputTypes';
import Button from '../../../../components/Button';
import formatCurrency from '../../../../utils/formatCurrency';
import cySelectors from '../../../../tests/cySelectors';

import {
  getEffectiveBounds,
  getGroupSelection,
  getSelectedItemIds,
  makeChildKey,
} from '../modifierSelection';

const ModifierGroup = ({
  modifier,
  groupKey,
  selection,
  handleSelectRadio,
  handleToggleCheckbox,
  handleSetQuantity,
}) => {
  const { label, component, modifierItems = [] } = modifier;
  // min/max are clamped to the items' combined multiMax capacity where that is tighter.
  const { minSelections, maxSelections } = getEffectiveBounds(modifier);
  const isRadio = component === 'RADIO';

  const group = getGroupSelection(selection, groupKey);
  const selectedIds = getSelectedItemIds(selection, groupKey);
  const cumulativeQuantity = Object.values(group).reduce((sum, qty) => sum + qty, 0);
  const maxReached = cumulativeQuantity >= maxSelections;

  // Preselect the first radio option whenever this radio group becomes active with no selection.
  useEffect(() => {
    if (isRadio && selectedIds.length === 0 && modifierItems.length) {
      handleSelectRadio(groupKey, modifierItems[0].itemId);
    }
  }, [isRadio, selectedIds.length, modifierItems, groupKey, handleSelectRadio]);

  const minHint = minSelections ? `At least ${minSelections}` : '';
  const maxHint = maxSelections ? `No more than ${maxSelections}` : '';
  const exactHint = minSelections === maxSelections ? `Exactly ${minSelections} selections` : false;
  const modifierHint =
    exactHint || `${minHint}${minSelections && maxSelections ? ', ' : ''}${maxHint}`;

  const changeQuantity = useCallback(
    (itemId, delta, multiMax) => {
      const current = group[itemId] || 0;
      const next = current + delta;
      if (next >= 1 && next <= multiMax && (delta < 0 || !maxReached)) {
        handleSetQuantity(groupKey, itemId, next);
      }
    },
    [group, groupKey, maxReached, handleSetQuantity],
  );

  return (
    <div
      className="item__modal__modifier"
      data-cy={isRadio ? cySelectors.MODIFIERS_RADIOS : cySelectors.MODIFIERS_CHECKBOXES}
    >
      <fieldset>
        <legend>{label}</legend>
        {!isRadio && modifierHint && <p className="item__modal__modifier__hint">{modifierHint}</p>}

        {modifierItems.map((item) => {
          const {
            itemId,
            itemName,
            label: itemLabel,
            itemPrice,
            multiMax,
            modifiers: nested,
          } = item;
          const isSelected = selectedIds.includes(itemId);
          const quantity = group[itemId] || 0;
          const canChangeQuantity = !isRadio && multiMax > 1 && isSelected;
          const inputId = `${groupKey}.${itemId}`;

          return (
            <div key={itemId} className="item__modal__modifier__choice">
              <label htmlFor={inputId}>
                <span className="item__modal__modifier__choice__name">{itemLabel || itemName}</span>

                {canChangeQuantity && (
                  <div className="item__modal__modifier__choice__quantities">
                    <Button
                      disabled={quantity <= 1}
                      small
                      moreRounded
                      onClick={() => changeQuantity(itemId, -1, multiMax)}
                      data-cy={cySelectors.CART_ITEM_DECREASE_BTN}
                    >
                      -
                    </Button>
                    <span
                      className="item__modal__modifier__choice__count"
                      data-cy={cySelectors.CART_ITEM_COUNT}
                    >
                      {quantity}
                    </span>
                    <Button
                      disabled={maxReached || quantity >= multiMax}
                      small
                      moreRounded
                      onClick={() => changeQuantity(itemId, 1, multiMax)}
                      data-cy={cySelectors.CART_ITEM_INCREASE_BTN}
                    >
                      +
                    </Button>
                  </div>
                )}

                {!!itemPrice && (
                  <span className="item__modal__modifier__choice__price">
                    +{formatCurrency(itemPrice)}
                  </span>
                )}

                <ChoiceInput
                  type={isRadio ? choiceInputTypes.RADIO : choiceInputTypes.CHECKBOX}
                  name={isRadio ? groupKey : inputId}
                  id={inputId}
                  value={itemId}
                  checked={isSelected}
                  disabled={!isRadio && maxReached && !isSelected}
                  data-cy={isRadio ? cySelectors.MODIFIERS_RADIO : cySelectors.MODIFIERS_CHECKBOX}
                  onChange={() =>
                    isRadio
                      ? handleSelectRadio(groupKey, itemId)
                      : handleToggleCheckbox(groupKey, itemId)
                  }
                />
              </label>

              {isSelected && nested?.length > 0 && (
                <div className="item__modal__modifier__nested">
                  {nested.map((child) => (
                    <ModifierGroup
                      key={child.modifierId}
                      modifier={child}
                      groupKey={makeChildKey(groupKey, itemId, child.modifierId)}
                      selection={selection}
                      handleSelectRadio={handleSelectRadio}
                      handleToggleCheckbox={handleToggleCheckbox}
                      handleSetQuantity={handleSetQuantity}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};

export default ModifierGroup;
